import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import { BarChart3, TrendingUp, BookOpen, Mic, Users, DollarSign } from './components/DeskeyIcons';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Skeleton } from './components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['hsl(199,89%,48%)', 'hsl(162,73%,46%)', 'hsl(36,92%,50%)', 'hsl(215,16%,35%)', 'hsl(173,58%,39%)'];

export default function AdminAnalytics() {
  const { user, token } = useApp();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/analytics/overview`, { params: { tenant_id: user?.tenant_id } , headers })
      .then(r => setAnalytics(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [user?.tenant_id]);

  if (loading) return <DashboardShell title="Analytics"><div className="grid grid-cols-2 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div></DashboardShell>;

  const a = analytics || {};

  const engagementData = [
    { name: 'Lessons\nPublished', value: a.lessons_published || 0 },
    { name: 'Recordings', value: a.recordings_completed || 0 },
    { name: 'Quiz\nAttempts', value: a.quiz_attempts || 0 },
    { name: 'Quizzes', value: a.quizzes_published || 0 },
  ];

  const userBreakdown = [
    { name: 'Teachers', value: a.active_teachers || 0 },
    { name: 'Students', value: a.active_students || 0 },
  ];

  const deviceData = [
    { name: 'Online', value: a.devices_online || 0 },
    { name: 'Offline', value: Math.max(0, (a.devices_total || 0) - (a.devices_online || 0)) },
  ];

  return (
    <DashboardShell title="Analytics" breadcrumb="Admin Dashboard">
      <div className="space-y-6 fade-in">
        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Teachers', value: a.active_teachers, color: COLORS[0] },
            { label: 'Students', value: a.active_students, color: COLORS[1] },
            { label: 'Billable', value: a.billable_students, color: COLORS[2] },
            { label: 'Lessons', value: a.lessons_published, color: COLORS[0] },
            { label: 'Recordings', value: a.recordings_completed, color: COLORS[1] },
            { label: 'Quiz Attempts', value: a.quiz_attempts, color: COLORS[2] },
          ].map(k => (
            <div key={k.label} className="kpi-card">
              <p className="kpi-label">{k.label}</p>
              <p className="kpi-number text-xl" style={{ color: k.color }}>{k.value ?? 0}</p>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Content Engagement</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={engagementData} barSize={36}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))', borderRadius: '0.75rem', fontSize: '0.75rem' }} />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">User Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={userBreakdown} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {userBreakdown.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))', borderRadius: '0.75rem', fontSize: '0.75rem' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Billing Projection */}
        <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4" style={{ color: 'hsl(var(--warning))' }} />
              Revenue Projection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              <div>
                <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Billable Students</p>
                <p className="text-2xl font-bold mt-1">{a.billable_students || 0}</p>
              </div>
              <div>
                <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Per Student/Month</p>
                <p className="text-2xl font-bold mt-1">₹5.00</p>
              </div>
              <div>
                <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Monthly Revenue</p>
                <p className="text-2xl font-bold mt-1" style={{ color: 'hsl(var(--success))' }}>₹{a.projected_monthly_billing?.toFixed(2) || '0.00'}</p>
              </div>
              <div>
                <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Annual Revenue</p>
                <p className="text-2xl font-bold mt-1" style={{ color: 'hsl(var(--warning))' }}>₹{a.projected_yearly_billing?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  );
}
